import {
  ACCESSIBILITY_ARGS,
  COMPONENT_STATE_ARGS,
  CONTAINER_ARGS,
  INPUT_MENU_ARGS,
  MENU_ARGS,
  Metadata,
  pick,
} from '../../../../utils/spds/metadata';
import { MENU_ITEM_SELECTOR, SEARCH_INPUT_SELECTOR } from '../../../../utils/spds/constants/menu';
import {
  MENU_SEARCH_THRESHOLD_DEFAULT,
  filterMenuItemsByText,
} from '../../../../utils/spds/menu-filter';
import { ROLE_LISTBOX } from '../../../../utils/spds/constants/aria';
import { applyBoldMatchingText, stripBolding } from '../../../../modifiers/bold-matching-text';
import { modifier } from 'ember-modifier';
import { tracked } from '@glimmer/tracking';
import FlexColumn from '../../layout/flex/column.gjs';
import FlexRow from '../../layout/flex/row.gjs';
import MenuSearch from './search.gjs';
import NoResults from './no-results.gjs';
import Super from '../../core/base.gjs';
import styles from './base.module.scss';

const METADATA = Metadata.define({
  description: `
Simple, easy to use menu component.

### Positioning

Providing the \`@anchorElement\` argument as a DOM element that is used to compute the \`top\` property for the menu. This can be
further modified by providing the \`@anchorOffset\` argument, which adjusts the distance between the top of the menu and the bottom of
the anchor element. Left/right alignment is determined by the width of the menu and the horizontal positioning of the anchor. If the anchor
position plus the menu width extends beyond the right edge of the viewport, the menu will automatically align to the right side of the anchor.
Otherwise, the menu defaults to aligning to the left side of the anchor. In either case, the position of the non-aligned side will be determined
in one of two ways:

* Default behavior causes the menu to flex its width to fit the container of the anchor element
* If the \`@fitContent\` argument is provided, the menu will restrict its width to the required space to fit its largest content

### In-menu search

When a menu renders at least \`@searchThreshold\` items (15 by default), a search field is automatically added to the top of the menu that
filters the items by text as the user types. This can be disabled per-menu by passing \`@searchable={{false}}\`, or forced on regardless of the
item count with \`@searchable={{true}}\`. When the active filter matches no items, a no-results message (customizable via \`@noResultsText\`) is shown.
  `,
  extends: [
    pick(ACCESSIBILITY_ARGS, [{ name: 'role', default: ROLE_LISTBOX }, 'ariaLabelledBy']),
    pick(COMPONENT_STATE_ARGS, ['open', { name: 'animated', default: true }]),
    pick(CONTAINER_ARGS, ['fitContent', { name: 'column', default: true }]),
    pick(MENU_ARGS, ['anchorElement', { name: 'anchorOffset', default: 4 }]),
    pick(INPUT_MENU_ARGS, [
      'multiselect',
      'noItemsDisplayed',
      'noResultsText',
      'searchable',
      'searchPlaceholder',
      'searchThreshold',
    ]),
  ],
  name: 'Menu Base',
});

export default class SpdsCoreMenuBase extends Super {
  static metadata = this.setMetadata(METADATA, import.meta.url);
  baseClass = 'spds-menu';
  classesToMap = ['animated', 'open', 'side'];
  propsToMap = ['top'];
  styles = styles;

  @tracked menuElement;
  @tracked side = 'left';
  @tracked itemCount = 0;
  @tracked top;
  @tracked visibleCount = 0;
  @tracked searchValue = '';

  get anchorBoundingRect() {
    return this.args.anchorElement?.getBoundingClientRect();
  }

  get anchorDistance() {
    return this.args.anchorOffset ?? this.constructor.metadata.getDefault('anchorOffset');
  }

  get canBePositioned() {
    return !!this.args.anchorElement && !!this.baseElement;
  }

  get column() {
    return this.argOrDefault('column');
  }

  get listComponent() {
    return this.column ? FlexColumn : FlexRow;
  }

  get listId() {
    return this.composeSubcomponentId('list');
  }

  get parentBoundingRect() {
    return this.args.anchorElement?.parentElement?.getBoundingClientRect();
  }

  get itemElements() {
    if (!this.baseElement) return [];

    return Array.from(this.baseElement.querySelectorAll(MENU_ITEM_SELECTOR));
  }

  // get propsToMap() {
  //   return [...super.propsToMap, 'top'];
  // }

  get role() {
    return this.argOrDefault('role');
  }

  get searchableIsSet() {
    let { searchable } = this.args;

    return searchable === Boolean(searchable);
  }

  get searchId() {
    return this.composeSubcomponentId('search');
  }

  get searchThreshold() {
    return this.args.searchThreshold ?? MENU_SEARCH_THRESHOLD_DEFAULT;
  }

  get searchEnabled() {
    if (this.searchableIsSet) return this.args.searchable;

    return this.itemCount >= this.searchThreshold;
  }

  get searchActive() {
    return this.searchEnabled && this.searchValue.length > 0;
  }

  get showNoResults() {
    if (this.args.noItemsDisplayed) return true;

    return this.searchActive && this.visibleCount === 0;
  }

  setMenuPosition = modifier(() => {
    if (!this.canBePositioned) return;

    let anchorBottom = this.anchorBoundingRect?.bottom;
    let menuRight = this.anchorBoundingRect?.x + this.baseElement?.getBoundingClientRect()?.width;
    let parentTop = this.parentBoundingRect?.top || 0;
    let top = anchorBottom - parentTop + this.anchorDistance;

    this.side = menuRight > window.innerWidth ? 'right' : 'left';
    this.top = `${top}px`;
  });

  countItems = modifier(() => {
    this.itemCount = this.itemElements.length;
  });

  focusSearch = modifier((element, [open]) => {
    if (!open || !this.searchEnabled) return;

    element.querySelector(SEARCH_INPUT_SELECTOR)?.focus();
  });

  runFilter() {
    let value = this.searchValue;
    let multiselect = !!this.args.multiselect;
    let items = this.itemElements;
    let matches = filterMenuItemsByText(items, value, { multiselect });

    items.forEach(el => {
      let display = multiselect ? el.parentElement : el;
      if (!display) return;

      if (value) {
        applyBoldMatchingText(display, value);
      } else {
        stripBolding(display.querySelector('span') ?? display);
      }
    });

    this.visibleCount = matches.length;
  }

  onSearchInput = evt => {
    this.searchValue = evt?.target?.value ?? '';
    this.runFilter();
  };

  clearSearch = () => {
    this.searchValue = '';
    this.runFilter();
    this.baseElement?.querySelector(SEARCH_INPUT_SELECTOR)?.focus();
  };

  <template>
    <FlexColumn
      @absolute={{true}}
      @fitContentWidth={{@fitContent}}
      aria-expanded={{@open}}
      class={{this.classes}}
      data-test-spds-menu
      id={{this.id}}
      style={{this.customProperties}}
      {{this.setupComponent}}
      {{this.setMenuPosition}}
      {{this.countItems}}
      {{this.focusSearch @open}}
      {{this.validateArguments}}
      ...attributes
    >
      {{#if this.searchEnabled}}
        <MenuSearch
          @ariaControls={{this.listId}}
          @flex="1"
          @id={{this.searchId}}
          @onClear={{this.clearSearch}}
          @onInput={{this.onSearchInput}}
          @placeholder={{@searchPlaceholder}}
          @value={{this.searchValue}}
        />
      {{/if}}

      <this.listComponent
        @gap="4px"
        aria-labelledby={{@ariaLabelledBy}}
        class="spds-menu-list"
        data-test-spds-menu-list
        id={{this.listId}}
        role={{this.role}}
      >
        {{yield}}

        {{#if this.showNoResults}}
          <NoResults @text={{@noResultsText}} />
        {{/if}}
      </this.listComponent>
    </FlexColumn>
  </template>
}

export const SPDS_CORE_MENU_BASE_METADATA = SpdsCoreMenuBase.metadata;
